import { EditorSelection, type StateCommand } from "@codemirror/state";
import { type KeyBinding } from "@codemirror/view";

import { dedentListItem, indentListItem } from "./list-indent";
import {
  clearEmptyPairOnEnter,
  clearOrphanMarkersOnEnter,
  completeRuleOnEnter,
  continueEmphasisOnEnter,
} from "./smart-enter";

/** Wrap (or unwrap) each selection range with `marker`/`closing`. This is the
 *  CodeMirror port of the old textarea `wrapSelection` helper — same toggle-off
 *  behaviour: if a selection is already wrapped, the markers are stripped. An
 *  empty selection inserts the pair with the cursor placed between the markers. */
export function wrapRange(marker: string, closing = marker): StateCommand {
  return ({ state, dispatch }) => {
    const tr = state.changeByRange((range) => {
      const sel = state.sliceDoc(range.from, range.to);
      // Toggle off when the selection already carries the markers.
      if (
        sel.length >= marker.length + closing.length &&
        sel.startsWith(marker) &&
        sel.endsWith(closing)
      ) {
        const inner = sel.slice(marker.length, sel.length - closing.length);
        return {
          changes: { from: range.from, to: range.to, insert: inner },
          range: EditorSelection.range(range.from, range.from + inner.length),
        };
      }
      // Toggle off when the markers sit just OUTSIDE the selection. This is the
      // state the command leaves behind: wrapping selects the inner text, and an
      // empty cursor lands between a fresh pair. Without this branch a second
      // Ctrl+B wrapped that inner text again — `word` → `**word**` → `****word****`
      // — instead of undoing the first, which is what pressing a toggle twice
      // has to mean.
      const before = state.sliceDoc(Math.max(0, range.from - marker.length), range.from);
      const after = state.sliceDoc(range.to, Math.min(state.doc.length, range.to + closing.length));
      if (before === marker && after === closing) {
        return {
          changes: [
            { from: range.from - marker.length, to: range.from },
            { from: range.to, to: range.to + closing.length },
          ],
          range: EditorSelection.range(range.from - marker.length, range.to - marker.length),
        };
      }
      return {
        changes: [
          { from: range.from, insert: marker },
          { from: range.to, insert: closing },
        ],
        range: EditorSelection.range(range.from + marker.length, range.to + marker.length),
      };
    });
    dispatch(state.update(tr, { scrollIntoView: true, userEvent: "input" }));
    return true;
  };
}

/** Wrap the selection as a markdown link `[sel](url)` with `url` selected, so the
 *  next keystroke replaces the placeholder — mirrors the old Cmd/Ctrl+K. Exported
 *  so the formatting toolbar (MarkdownEditor) can run the identical command a
 *  mouse click triggers instead of duplicating the insertion logic. */
export const insertLink: StateCommand = ({ state, dispatch }) => {
  const tr = state.changeByRange((range) => {
    const sel = state.sliceDoc(range.from, range.to);
    const insert = `[${sel}](url)`;
    const urlStart = range.from + 1 + sel.length + 2; // past "[sel]("
    return {
      changes: { from: range.from, to: range.to, insert },
      range: EditorSelection.range(urlStart, urlStart + 3),
    };
  });
  dispatch(state.update(tr, { scrollIntoView: true, userEvent: "input" }));
  return true;
};

/** Insert an already-uploaded image as `![](path)` at the cursor (replacing any
 *  current selection), cursor landing just past the closing paren. Used by the
 *  toolbar's "Insert image" button and by paste/drop-of-an-image-file, both of
 *  which upload the file first and then run this command with the resulting
 *  path — see `MarkdownEditor`'s `insertImageFromFile`. The live-preview layer
 *  (`cm/live-preview.ts`) already renders any `![alt](url)` as an inline image
 *  widget, so a pasted/dropped/toolbar-inserted image shows up immediately. */
export function insertImage(path: string): StateCommand {
  return ({ state, dispatch }) => {
    const tr = state.changeByRange((range) => {
      const insert = `![](${path})`;
      return {
        changes: { from: range.from, to: range.to, insert },
        range: EditorSelection.cursor(range.from + insert.length),
      };
    });
    dispatch(state.update(tr, { scrollIntoView: true, userEvent: "input" }));
    return true;
  };
}

/** Insert an empty GFM table of `rows` × `cols` at the cursor, caret landing in
 *  the first header cell so the next keystroke starts naming the column.
 *
 *  `rows` counts BODY rows — the header is always present, because a GFM table
 *  without one does not parse. A table has to start on its own line, so a
 *  leading newline is added when the caret is mid-line; the live-preview layer
 *  then renders it as a real `<table>` the moment the caret leaves. */
export function insertTable(rows: number, cols: number): StateCommand {
  return ({ state, dispatch }) => {
    const range = state.selection.main;
    const row = (cell: string) => `|${` ${cell} |`.repeat(cols)}`;
    const table = [row("  "), row("---"), ...Array.from({ length: rows }, () => row("  "))].join("\n");
    // The table must stand ALONE, separated by a blank line from whatever is
    // around it. GFM would happily let it butt against the paragraph above, but
    // then it is not its own block — and Preview's editable grid, like every
    // other block-level treatment, keys off the block. So pad to a blank line on
    // each side, adding only what is missing.
    const before = state.sliceDoc(0, range.from);
    const after = state.sliceDoc(range.to, state.doc.length);
    const lead = !before || /\n[ \t]*\n$/.test(before) ? "" : before.endsWith("\n") ? "\n" : "\n\n";
    const trail = !after || /^[ \t]*\n[ \t]*\n/.test(after) ? "\n" : after.startsWith("\n") ? "\n" : "\n\n";
    const insert = `${lead}${table}${trail}`;
    // Two chars past the first `| ` is the first header cell's content.
    const caret = range.from + lead.length + 2;
    dispatch(
      state.update({
        changes: { from: range.from, to: range.to, insert },
        selection: EditorSelection.cursor(caret),
        scrollIntoView: true,
        userEvent: "input",
      }),
    );
    return true;
  };
}

/** Formatting shortcuts matching the previous editor: Cmd/Ctrl+B bold, +I italic,
 *  +E inline code, +`=`/Shift+H highlight, +K link, +S save. */
export function editingBindings(onSave: () => void): KeyBinding[] {
  return [
    { key: "Mod-b", run: wrapRange("**"), preventDefault: true },
    { key: "Mod-i", run: wrapRange("*"), preventDefault: true },
    { key: "Mod-e", run: wrapRange("`"), preventDefault: true },
    { key: "Mod-=", run: wrapRange("=="), preventDefault: true },
    { key: "Shift-Mod-h", run: wrapRange("=="), preventDefault: true },
    { key: "Mod-k", run: insertLink, preventDefault: true },
    // Enter, ahead of markdown's continue-list. Both commands return false when
    // they do not apply, so a list/quote/plain Enter falls straight through to
    // `markdownKeymap`. Rule completion goes first: a line of `___` is not
    // emphasis, and checking it is cheaper than resolving the syntax tree.
    { key: "Enter", run: completeRuleOnEnter },
    // ORDER: clearing an empty pair must beat continuing one. `**|**` satisfies
    // both — it IS an emphasis span the caret sits inside — and if continuation
    // won, a second Enter would reopen the run forever and strand `****`.
    { key: "Enter", run: clearEmptyPairOnEnter },
    { key: "Enter", run: clearOrphanMarkersOnEnter },
    { key: "Enter", run: continueEmphasisOnEnter },
    // Tab / Shift-Tab inside a LIST indent by the parent's content column rather
    // than a flat indent unit — the only indent markdown actually nests on. Both
    // decline outside a list item, so `indentWithTab` still covers everything
    // else (and Tab still moves focus when there is nothing to indent).
    { key: "Tab", run: indentListItem },
    { key: "Shift-Tab", run: dedentListItem },
    {
      key: "Mod-s",
      preventDefault: true,
      run: () => {
        onSave();
        return true;
      },
    },
  ];
}
